// s233 — DEADLINE-COUNTDOWN-1 (D-232.56): THE deadline countdown. ONE calculator (G7) — the executor task list's Due
// column and the drawer's due line call it, and Today's "Coming dates" will (D-232.91: Today is not in the product yet).
// The looks, words and colours are board 12's (`Home.dc.html` v417, `dueView` / `dueLine`, about lines 2363–2382):
//   more than 14 days → the date · 8–14 → "in N days" · 7 or fewer → amber "in N days" / "Today"
//   past → brick "Overdue · N days" · done or set aside → the date, greyed · no date → its placeholder ("No fixed date").
// Days are whole calendar days between date-only values in UTC — never wall-clock arithmetic (the s150 off-by-one class).
import type { ResolvedDue } from "./due-date";
import { WARM } from "../brand";

export type DueTone = "date" | "near" | "overdue" | "closed" | "none";

/** The board's colours for each look. */
export const DUE_COLOR: Readonly<Record<DueTone, string>> = {
  date: WARM.charcoal,
  near: "#B8860B",
  overdue: "#8B1E3F",
  closed: WARM.taupe,
  none: WARM.muted, // #4A4640 — s233 (D-233.37): the muted text, darkened everywhere (board 12 line 2437)
};

export type DueLook = {
  text: string;
  tone: DueTone;
  color: string;
  /** The due date (yyyy-mm-dd), or null when there is none. */
  dateISO: string | null;
  /** Whole days from today to the due date (negative once past); null when closed or undated. */
  left: number | null;
};

/** Statuses that close a task: done, or set aside (by the executor, or by the plan). */
const CLOSED = new Set(["done", "dismissed", "auto_resolved"]);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const NO_DATE = "No fixed date";

const utc = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};

/** "2 Oct 2026" — the board's way of writing a date. Pure. */
export function formatDueDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** Whole calendar days from one date-only value to another. Pure. */
export function daysBetween(fromISO: string, toISO: string): number {
  return Math.round((utc(toISO) - utc(fromISO)) / 86_400_000);
}

/** Today's calendar date where the viewer is — the day the executor is living in. */
export function todayISO(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** A task's deadline, as it reads today. Pure. */
export function dueLook(due: ResolvedDue | undefined | null, status: string, today: string): DueLook {
  const iso = due?.kind === "date" ? due.dateISO : null;
  if (!iso) {
    return { text: due?.text || NO_DATE, tone: "none", color: DUE_COLOR.none, dateISO: null, left: null };
  }
  const date = formatDueDate(iso);
  if (CLOSED.has(status)) return { text: date, tone: "closed", color: DUE_COLOR.closed, dateISO: iso, left: null };
  const left = daysBetween(today, iso);
  const at = (text: string, tone: DueTone) => ({ text, tone, color: DUE_COLOR[tone], dateISO: iso, left });
  if (left < 0) return at(`Overdue · ${-left} ${left === -1 ? "day" : "days"}`, "overdue");
  if (left === 0) return at("Today", "near");
  if (left <= 7) return at(`in ${left} ${left === 1 ? "day" : "days"}`, "near");
  if (left <= 14) return at(`in ${left} days`, "date");
  return at(date, "date");
}

/**
 * The drawer's due line: "Due 2 Oct 2026 — 30 days after the death" where the deadline counts from the death;
 * "Due 30 Apr 2027" for any other anchor, or a date read off the planner's record; null with no date. Pure.
 */
export function dueLine(
  anchor: string, due: ResolvedDue | undefined | null, deathISO: string | null, opts: { fromRecord?: boolean } = {},
): string | null {
  const iso = due?.kind === "date" ? due.dateISO : null;
  if (!iso) return null;
  const base = `Due ${formatDueDate(iso)}`;
  const death = deathISO?.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? null; // a stored death date may carry a time
  if (anchor !== "death" || !death || opts.fromRecord) return base;
  const n = daysBetween(death, iso);
  return `${base} — ${n} ${n === 1 ? "day" : "days"} after the death`;
}
